/**
 * Tiny CSV helpers (Phase 4 export feature).
 *
 * No external dep — RFC 4180 compliant escaping is small enough to hand-roll.
 */

/** Escape a single CSV field per RFC 4180. */
export function escapeCsvField(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (s === '') return '';
  // Quote if it contains comma, quote, CR, or LF.
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Build a CSV string from an array of header keys and rows of objects. */
export function buildCsv<T extends Record<string, unknown>>(
  headers: Array<{ key: keyof T & string; label: string }>,
  rows: T[],
): string {
  const headerLine = headers.map((h) => escapeCsvField(h.label)).join(',');
  const bodyLines = rows.map((row) =>
    headers.map((h) => escapeCsvField(row[h.key])).join(','),
  );
  return [headerLine, ...bodyLines].join('\r\n');
}

/**
 * Trigger a browser download of a CSV blob.
 *
 * No-ops in non-browser environments so unit tests can still import the
 * module safely.
 */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  // Prepend a UTF-8 BOM so Excel opens utf-8 cleanly.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoking so Safari has time to read the URL.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
